import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";

import EcoIcon, { EcoIconName } from "../../../components/eco-icon/EcoIcon";
import EcoPageHeader from "../../../components/eco-page-header/EcoPageHeader";
import { useGoBack } from "../../../hooks/useGoBack";
import type { RootState } from "../../../store/store";
import { isAdminFromToken } from "../../../utils/authToken";

import {
  DP_EMPLOYEES_PAGE,
  DP_EMPLOYEE_NEW_PAGE,
  DP_EXPIRING_DOCUMENTS_PAGE,
  DP_EXPIRING_PROBATIONS_PAGE,
  DP_FGTS_PAGE,
  DP_PAYROLL_PAGE,
  DP_PROVISIONS_PAGE,
  DP_LEAVES_PAGE,
  DP_PROCESSES_PAGE,
  DP_SETTINGS_PAGE,
  DP_VACATIONS_PAGE,
  DP_ROSTERS_PAGE,
  DP_TIMESHEETS_PAGE,
  DP_ADJUSTMENTS_PAGE,
} from "../routes";
import { getProcessesSummaryApi } from "../api/processesApi";
import { getDocumentsSummaryApi } from "../api/documentsApi";
import { getVacationsSummaryApi } from "../api/vacationsApi";
import { getLeavesSummaryApi } from "../api/leavesApi";
import { getTimesheetsSummaryApi } from "../api/timekeepingApi";
import { getProbationSummaryApi } from "../api/contractsApi";
import { getStoreScheduleSummaryApi } from "../api/scheduleApi";
import { MONTH_LABELS } from "../utils/format";
import { EVALUATION_FORMS_ADMIN_PAGE } from "../../evaluations/routes";
import { NR1_DASHBOARD_PAGE, NR1_LINKS_ADMIN_PAGE } from "../../nr1/routes";
import "./DpEco.css";

type Tone = "bad" | "warn" | "info" | "good" | "muted";

type ModuleCard = {
  title: string;
  description: string;
  icon: EcoIconName;
  onClick?: () => void;
  /** Módulo previsto nos planos C e E; aparece desabilitado para dar a visão do todo. */
  comingSoon?: boolean;
  /** Contador exibido no card (ex.: documentos vencidos). */
  counter?: { text: string; tone: Tone };
};

type Alert = { key: string; title: string; desc: string; tone: Tone; onClick: () => void };

const plural = (n: number, one: string, many: string) => `${n} ${n === 1 ? one : many}`;

export default function DpPage() {
  const navigate = useNavigate();
  const token = useSelector((state: RootState) => state.auth.accessToken);
  // Para o papel DP o hub é a tela inicial: o "voltar" só faz sentido para o ADMIN.
  const isAdmin = useMemo(() => isAdminFromToken(token), [token]);
  const goBack = useGoBack("/");

  const [processes, setProcesses] = useState<{ open: number; overdue: number } | null>(null);
  const [documents, setDocuments] = useState<{ expired: number; expiring: number } | null>(null);
  const [vacations, setVacations] = useState<{ expired: number; expiring: number } | null>(null);
  const [leaves, setLeaves] = useState<{ active: number } | null>(null);
  const [probations, setProbations] = useState<{ days: number; firstPeriodEnding: number; ending: number } | null>(
    null
  );
  /** Lojas ativas sem posição: o raio da avaliação de loja não é conferido nelas. */
  const [storesWithoutLocation, setStoresWithoutLocation] = useState<number | null>(null);
  const [ponto, setPonto] = useState<{
    enabled: boolean;
    pendingAdjustments: number;
    openPrevMonth: number;
    prevMonth: { year: number; month: number };
    contested: number;
    unsignedOverdue: number;
    signatureDeadlineDays: number;
  } | null>(null);

  useEffect(() => {
    if (!token) return;
    // Contadores são auxiliares: falha aqui não impede o uso do hub.
    getProcessesSummaryApi(token)
      .then(setProcesses)
      .catch(() => setProcesses(null));
    getDocumentsSummaryApi(token)
      .then(setDocuments)
      .catch(() => setDocuments(null));
    getVacationsSummaryApi(token)
      .then(setVacations)
      .catch(() => setVacations(null));
    getLeavesSummaryApi(token)
      .then(setLeaves)
      .catch(() => setLeaves(null));
    getTimesheetsSummaryApi(token)
      .then(setPonto)
      .catch(() => setPonto(null));
    getProbationSummaryApi(token)
      .then(setProbations)
      .catch(() => setProbations(null));
    getStoreScheduleSummaryApi(token)
      .then((summary) => setStoresWithoutLocation(summary.stores.filter((st) => !st.hasLocation).length))
      .catch(() => setStoresWithoutLocation(null));
  }, [token]);

  // "Atenção hoje": só o que pede ação, a partir dos mesmos resumos dos contadores.
  const alerts = useMemo<Alert[]>(() => {
    const list: Alert[] = [];
    if (documents && documents.expired + documents.expiring > 0) {
      list.push({
        key: "docs",
        title: documents.expired > 0 ? plural(documents.expired, "documento vencido", "documentos vencidos") : plural(documents.expiring, "documento a vencer", "documentos a vencer"),
        desc: documents.expired > 0 && documents.expiring > 0 ? `e ${documents.expiring} a vencer` : "ASO, NRs e termos com validade",
        tone: documents.expired > 0 ? "bad" : "warn",
        onClick: () => navigate(DP_EXPIRING_DOCUMENTS_PAGE()),
      });
    }
    if (probations && probations.firstPeriodEnding + probations.ending > 0) {
      list.push({
        key: "experiencias",
        title:
          probations.ending > 0
            ? plural(probations.ending, "experiência terminando", "experiências terminando")
            : plural(probations.firstPeriodEnding, "experiência com o 1º período terminando", "experiências com o 1º período terminando"),
        desc:
          probations.ending > 0 && probations.firstPeriodEnding > 0
            ? `e ${probations.firstPeriodEnding} com o 1º período terminando, nos próximos ${probations.days} dias`
            : probations.ending > 0
              ? "decidir se efetiva ou encerra"
              : "decidir se prorroga",
        tone: "warn",
        onClick: () => navigate(DP_EXPIRING_PROBATIONS_PAGE()),
      });
    }
    if (processes && processes.overdue > 0) {
      list.push({
        key: "proc",
        title: plural(processes.overdue, "processo com atraso", "processos com atraso"),
        desc: `de ${plural(processes.open, "processo em andamento", "processos em andamento")}`,
        tone: "bad",
        onClick: () => navigate(DP_PROCESSES_PAGE()),
      });
    }
    if (vacations && vacations.expired + vacations.expiring > 0) {
      list.push({
        key: "vac",
        title: vacations.expired > 0 ? plural(vacations.expired, "período de férias vencido", "períodos de férias vencidos") : plural(vacations.expiring, "período de férias a vencer", "períodos de férias a vencer"),
        desc: vacations.expired > 0 && vacations.expiring > 0 ? `e ${vacations.expiring} a vencer em 60 dias` : "concessivo terminando em até 60 dias",
        tone: vacations.expired > 0 ? "bad" : "warn",
        onClick: () => navigate(DP_VACATIONS_PAGE()),
      });
    }
    if (leaves && leaves.active > 0) {
      list.push({
        key: "leaves",
        title: plural(leaves.active, "afastado hoje", "afastados hoje"),
        desc: "atestados, licenças e INSS",
        tone: "info",
        onClick: () => navigate(DP_LEAVES_PAGE()),
      });
    }
    if (ponto?.enabled && ponto.pendingAdjustments > 0) {
      list.push({
        key: "ponto-ajustes",
        title: plural(ponto.pendingAdjustments, "ajuste de ponto pendente", "ajustes de ponto pendentes"),
        desc: "pedidos de correção dos colaboradores",
        tone: "warn",
        onClick: () => navigate(DP_ADJUSTMENTS_PAGE()),
      });
    }
    if (ponto?.enabled && ponto.openPrevMonth > 0) {
      list.push({
        key: "ponto-fechar",
        title: plural(ponto.openPrevMonth, "espelho de ponto a fechar", "espelhos de ponto a fechar"),
        desc: `${MONTH_LABELS[ponto.prevMonth.month - 1]} de ${ponto.prevMonth.year} ainda aberto`,
        tone: "info",
        onClick: () => navigate(`${DP_TIMESHEETS_PAGE()}?ano=${ponto.prevMonth.year}&mes=${ponto.prevMonth.month}&situacao=ABERTO`),
      });
    }
    if (ponto?.enabled && ponto.contested > 0) {
      list.push({
        key: "ponto-contestados",
        title: plural(ponto.contested, "espelho de ponto contestado", "espelhos de ponto contestados"),
        desc: "o colaborador discordou do espelho fechado",
        tone: "warn",
        onClick: () => navigate(`${DP_TIMESHEETS_PAGE()}?ano=${ponto.prevMonth.year}&mes=${ponto.prevMonth.month}&assinatura=CONTESTADO`),
      });
    }
    if (ponto?.enabled && ponto.unsignedOverdue > 0) {
      list.push({
        key: "ponto-sem-assinatura",
        title: plural(ponto.unsignedOverdue, "espelho sem assinatura", "espelhos sem assinatura"),
        desc: `há mais de ${ponto.signatureDeadlineDays} dias do fechamento`,
        tone: "info",
        onClick: () => navigate(`${DP_TIMESHEETS_PAGE()}?ano=${ponto.prevMonth.year}&mes=${ponto.prevMonth.month}&assinatura=ATRASADO`),
      });
    }
    if (storesWithoutLocation) {
      list.push({
        key: "lojas-sem-posicao",
        title: plural(storesWithoutLocation, "loja sem posição cadastrada", "lojas sem posição cadastrada"),
        desc: "o raio da avaliação de loja não é conferido nelas",
        tone: "info",
        onClick: () => navigate(`${DP_SETTINGS_PAGE()}?aba=lojas`),
      });
    }
    return list;
  }, [documents, processes, vacations, leaves, ponto, probations, storesWithoutLocation, navigate]);

  const loadedSummaries = [processes, documents, vacations, leaves, ponto, probations].some((s) => s !== null);

  const sections = useMemo<{ id: string; title: string; modules: ModuleCard[] }[]>(
    () => [
      {
        id: "pessoas",
        title: "Pessoas e vínculos",
        modules: [
          {
            title: "Colaboradores",
            description: "Cadastro, dependentes e contrato com histórico de alterações.",
            onClick: () => navigate(DP_EMPLOYEES_PAGE()),
            icon: "users",
          },
          {
            title: "Admissão e desligamento",
            description: "Checklists com etapas, prazos e responsáveis.",
            onClick: () => navigate(DP_PROCESSES_PAGE()),
            counter:
              processes === null
                ? undefined
                : processes.overdue > 0
                  ? { text: `${processes.open} em andamento, ${processes.overdue} com atraso`, tone: "bad" }
                  : { text: `${processes.open} em andamento`, tone: "info" },
            icon: "checkSquare",
          },
          {
            title: "Documentos a vencer",
            description: "ASO, NRs e demais documentos com validade.",
            onClick: () => navigate(DP_EXPIRING_DOCUMENTS_PAGE()),
            counter:
              documents === null
                ? undefined
                : documents.expired > 0
                  ? { text: `${documents.expired} vencido(s), ${documents.expiring} a vencer`, tone: "bad" }
                  : documents.expiring > 0
                    ? { text: `${documents.expiring} a vencer`, tone: "warn" }
                    : { text: "Nada vencendo", tone: "good" },
            icon: "file",
          },
          {
            title: "Cadastros",
            description: "Empregadores (CNPJ), cargos com CBO, centros de custo, lojas e turnos, feriados, tipos de documento e checklists.",
            onClick: () => navigate(DP_SETTINGS_PAGE()),
            icon: "gear",
          },
        ],
      },
      {
        id: "tempo",
        title: "Tempo de trabalho",
        modules: [
          {
            title: "Férias",
            description: "Períodos aquisitivos, saldo, programação e aviso.",
            onClick: () => navigate(DP_VACATIONS_PAGE()),
            counter:
              vacations === null
                ? undefined
                : vacations.expired > 0
                  ? { text: `${vacations.expired} vencido(s), ${vacations.expiring} a vencer`, tone: "bad" }
                  : vacations.expiring > 0
                    ? { text: `${vacations.expiring} a vencer em 60 dias`, tone: "warn" }
                    : { text: "Nada vencendo", tone: "good" },
            icon: "calendar",
          },
          {
            title: "Afastamentos",
            description: "Atestados, licenças e INSS, com a regra dos 15 dias.",
            onClick: () => navigate(DP_LEAVES_PAGE()),
            counter:
              leaves === null
                ? undefined
                : leaves.active > 0
                  ? { text: `${leaves.active} afastado(s) hoje`, tone: "warn" }
                  : { text: "Ninguém afastado hoje", tone: "good" },
            icon: "activity",
          },
          {
            title: "Escala da loja",
            description: "Folgas e turnos do mês por loja: sugestão automática, publicação e pedidos de troca.",
            onClick: () => navigate(DP_ROSTERS_PAGE()),
            icon: "store",
          },
          {
            title: "Ponto e jornada",
            description: "Espelho do mês por loja, ajustes pedidos pelos colaboradores e fechamento.",
            onClick: () => navigate(DP_TIMESHEETS_PAGE()),
            counter:
              ponto === null
                ? undefined
                : !ponto.enabled
                  ? { text: "Desligado (Administração do sistema)", tone: "muted" }
                  : ponto.pendingAdjustments > 0
                    ? { text: `${ponto.pendingAdjustments} ajuste(s) pendente(s)`, tone: "warn" }
                    : ponto.contested > 0
                      ? { text: `${ponto.contested} contestação(ões)`, tone: "warn" }
                    : ponto.openPrevMonth > 0
                      ? { text: `${ponto.openPrevMonth} espelho(s) do mês anterior a fechar`, tone: "info" }
                      : ponto.unsignedOverdue > 0
                        ? { text: `${ponto.unsignedOverdue} sem assinatura há mais de ${ponto.signatureDeadlineDays} dias`, tone: "info" }
                        : { text: "Em dia", tone: "good" },
            icon: "clock",
          },
        ],
      },
      {
        id: "folha",
        title: "Folha e recolhimentos",
        modules: [
          {
            title: "Folha para conferência",
            description: "Pagamento mensal calculado para conferir a folha do contador; exporta CSV.",
            onClick: () => navigate(DP_PAYROLL_PAGE()),
            icon: "dollar",
          },
          {
            title: "Recolhimentos de FGTS e INSS",
            description: "Controle mensal das guias por competência.",
            onClick: () => navigate(DP_FGTS_PAGE()),
            icon: "creditCard",
          },
          {
            title: "Provisões",
            description: "13º, férias, 1/3 e FGTS devidos por colaborador; foto mensal no fechamento da folha.",
            onClick: () => navigate(DP_PROVISIONS_PAGE()),
            icon: "layers",
          },
        ],
      },
      {
        id: "nr1",
        title: "NR1 — riscos psicossociais",
        modules: [
          {
            title: "Formulários NR1",
            description: "Questionários da pesquisa anônima, com as perguntas por dimensão.",
            onClick: () => navigate(EVALUATION_FORMS_ADMIN_PAGE()),
            icon: "helpCircle",
          },
          {
            title: "Links anônimos",
            description: "Links de uso único para pesquisa por loja.",
            onClick: () => navigate(NR1_LINKS_ADMIN_PAGE()),
            icon: "link",
          },
          {
            title: "Riscos por loja",
            description: "Painel com as respostas anônimas.",
            onClick: () => navigate(NR1_DASHBOARD_PAGE()),
            icon: "pieChart",
          },
        ],
      },
    ],
    [navigate, processes, documents, vacations, leaves, ponto]
  );

  if (!token) return null;

  return (
    <div className="dpePage">
      <main className="dpePage__main">
        <EcoPageHeader
          crumbs={[{ label: "Início", onClick: () => navigate("/") }, { label: "Departamento Pessoal" }]}
          title="Departamento Pessoal"
          subtitle="Pessoas contratadas pela CLT: contratos, tempo de trabalho, documentos e folha."
          onBack={isAdmin ? goBack : undefined}
          backLabel="Voltar ao início"
          actions={
            <>
              <button type="button" className="ecoBtn" onClick={() => navigate(DP_EMPLOYEES_PAGE())}>
                <EcoIcon name="search" size={18} />
                Buscar colaborador
              </button>
              <button type="button" className="ecoBtn ecoBtn--primary" onClick={() => navigate(DP_EMPLOYEE_NEW_PAGE())}>
                <EcoIcon name="plus" size={18} />
                Novo colaborador
              </button>
            </>
          }
        />

        <section className="dpeSection" aria-labelledby="dp-atencao">
          <h2 id="dp-atencao" className="dpeSection__title">Atenção hoje</h2>
          <div className="dpeAlerts">
            {alerts.length > 0 ? (
              alerts.map((a) => (
                <button key={a.key} type="button" className="dpeAlert" onClick={a.onClick}>
                  <span className={`dpeAlert__dot dpeAlert__dot--${a.tone}`} aria-hidden="true" />
                  <span className="dpeAlert__text">
                    <span className="dpeAlert__title">{a.title}</span>
                    <span className="dpeAlert__desc">{a.desc}</span>
                  </span>
                </button>
              ))
            ) : (
              <div className="dpeAlert dpeAlert--calm">
                <span className="dpeAlert__dot dpeAlert__dot--good" aria-hidden="true" />
                <span className="dpeAlert__text">
                  <span className="dpeAlert__title">{loadedSummaries ? "Tudo em dia" : "Carregando..."}</span>
                  <span className="dpeAlert__desc">
                    {loadedSummaries ? "Nenhum documento, processo, férias ou afastamento pedindo atenção." : "Conferindo pendências do DP."}
                  </span>
                </span>
              </div>
            )}
          </div>
        </section>

        {sections.map((section) => (
          <section key={section.id} className="dpeSection" aria-labelledby={`dp-${section.id}`}>
            <h2 id={`dp-${section.id}`} className="dpeSection__title">{section.title}</h2>
            <div className="dpeModules">
              {section.modules.map((m) => (
                <button
                  key={m.title}
                  type="button"
                  className="dpeModule"
                  onClick={m.onClick}
                  disabled={m.comingSoon}
                  title={m.comingSoon ? "Em desenvolvimento" : undefined}
                >
                  <span className="dpeModule__icon">
                    <EcoIcon name={m.icon} size={20} strokeWidth={1.8} />
                  </span>
                  <span className="dpeModule__text">
                    <span className="dpeModule__title">{m.title}</span>
                    <span className="dpeModule__desc">{m.description}</span>
                    {m.counter ? (
                      <span className={`dpeModule__counter dpeModule__counter--${m.counter.tone}`}>{m.counter.text}</span>
                    ) : null}
                  </span>
                  {!m.comingSoon ? <EcoIcon name="chevronRight" className="dpeModule__chevron" /> : null}
                </button>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
}
